import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'utils/with-i18next';
import { MonitorSpeaker } from 'lucide-react';

const HeroSection = ({ t, onHeightChange, onNhrChange }) => {
  const heroRef = useRef(null);
  const [nhr, setNhr] = useState(false);
  const [waveHeights, setWaveHeights] = useState(Array(12).fill(16));

  useEffect(() => {
    const now = new Date().getHours();
    if (now >= 6 && now < 20) {
      setNhr(true);
      onNhrChange?.(true);
    }
  }, [onNhrChange]);

  useEffect(() => {
    if (heroRef.current && onHeightChange) {
      onHeightChange(heroRef.current.offsetHeight);
    }
  }, [onHeightChange]);

  // Dynamic waveform animation
  useEffect(() => {
    let timeoutId;
    const animate = () => {
      setWaveHeights(
        Array(12).fill(0).map(() => Math.random() * 24 + 8)
      );
      timeoutId = setTimeout(animate, 150);
    };
    animate();
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <section
      className={`relative bg-background text-white overflow-hidden pb-16 ${nhr ? 'leon' : ''}`}
      ref={heroRef}
    >
      {/* Title & Description */}
      <div className="relative z-10 pt-16 md:pt-24 px-6 md:px-12 text-center">
        <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent animate-slide-up">
          {t('hero.title')}
        </h1>
        <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          {t('hero.description')}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <a
            href="/myChannel"
            className="px-8 py-3 bg-primary rounded-lg font-semibold text-black hover:bg-primary/90 transition-all duration-300 shadow-lg shadow-primary/25 hover:shadow-primary/40"
          >
            {t('hero.ctaPlayer') || '플레이어 열기'}
          </a>
          <a
            href="https://remote.bgms.live"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-3 border border-gray-700 rounded-lg font-semibold text-gray-300 hover:border-gray-500 hover:text-white transition-all duration-300"
          >
            {t('hero.ctaRemote') || '리모컨 사용하기'}
          </a>
        </div>
      </div>

      {/* Animation Area - Cards with Connection Lines */}
      <div className="relative mt-12 md:mt-16 px-4 md:px-12 pb-8 animate-scale-in" style={{ animationDelay: '0.4s' }}>
        <div className="relative z-10 flex flex-col md:flex-row items-center md:items-stretch justify-center gap-8 md:gap-0 max-w-4xl mx-auto">
          {/* Left Side - User Remote Controls Card */}
          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-xl p-4 md:p-6 hover:border-primary/50 transition-all duration-300">
            <h3 className="text-sm font-semibold text-center text-gray-300 mb-4">{t('hero.remotes') || '리모컨'}</h3>
            <div className="flex flex-row md:flex-col justify-center gap-3">
              {[1, 2, 3].map((num) => (
                <div
                  key={num}
                  className="flex flex-col items-center gap-1"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-gray-800/80 border border-gray-700 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <rect x="7" y="2" width="10" height="20" rx="2" strokeWidth="1.5" />
                      <circle cx="12" cy="7" r="2" fill="currentColor" />
                      <line x1="9" y1="12" x2="15" y2="12" strokeWidth="1.5" strokeLinecap="round" />
                      <line x1="9" y1="15" x2="15" y2="15" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="12" cy="19" r="1" fill="currentColor" />
                    </svg>
                  </div>
                  <span className="text-[8px] md:text-[10px] text-gray-500">{t('hero.user')} {num}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Connection Line - Desktop (single line) */}
          <div className="hidden md:flex items-center w-16">
            <div className="w-full h-0.5 bg-primary"></div>
          </div>

          {/* Mobile Connection Line */}
          <div className="flex md:hidden flex-col items-center">
            <div className="w-0.5 h-8 bg-primary"></div>
          </div>

          {/* Right Side - Player Screen */}
          <div className="w-full md:w-auto flex items-center">
            <div className="relative w-full md:w-64 bg-gray-900/80 backdrop-blur-sm border border-gray-800 rounded-xl p-6 hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-primary rounded-xl flex items-center justify-center">
                  <MonitorSpeaker className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-white break-words">{t('hero.player')}</h3>
                <p className="text-sm text-gray-400 break-words">{t('hero.playerDesc')}</p>
                {/* Dynamic waveform */}
                <div className="mt-4 h-8 flex items-end justify-center gap-1">
                  {waveHeights.map((height, i) => (
                    <div
                      key={i}
                      className="w-1 bg-primary rounded-full transition-all duration-150"
                      style={{ height: `${height}px` }}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Background gradient effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
    </section>
  );
};

HeroSection.propTypes = {
  t: PropTypes.func.isRequired,
  onHeightChange: PropTypes.func,
  onNhrChange: PropTypes.func,
};

export default withTranslation('index')(HeroSection);
